// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme } from "@mui/material/styles";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import CustomDatePicker from "src/components/shared/inputs/CustomDatePicker/CustomDatePicker";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import CustomAutocomplete from "src/components/shared/inputs/CustomAutocomplete/CustomAutocomplete";
import TrialPrescriptionInputs from "src/components/containers/shared/TrialPrescriptionInputs/TrialPrescriptionInputs";
import Alert from "src/components/shared/alerts/Alert/Alert";
import { SelectChangeEvent } from "@mui/material/Select";

// Utils
import { getLocale } from "src/utils/locale/localeHelper";
import { calculateYearDifference, getISODate } from "src/utils/date/dateHelper";
import { getBirthdateOnly, getSexLabel } from "src/utils/emr/emrMatchHelper";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Config
import { allLanguageMap, priorityLanguageMap } from "src/config/languages";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { AddEditPatientType, PatientType } from "src/services/patients/types";
import { UnassignedEMRType } from "src/services/emr/types";

type CreateEMRPatientModalType = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: AddEditPatientType) => void;
  allPatients: Array<PatientType> | undefined;
  exam: UnassignedEMRType;
};

const CreateEMRPatientModal = ({
  open,
  onClose,
  onConfirm,
  allPatients,
  exam,
}: CreateEMRPatientModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const [existingPatient, setExistingPatient] = useState<PatientType>();

  const existingBirthDate = useMemo(
    () => existingPatient?.dateOfBirth?.slice(0, 10) ?? null,
    [existingPatient?.dateOfBirth]
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [healthCareId, setHealthCareId] = useState("");
  const [sex, setSex] = useState({ displayName: "", value: "" });
  const [requiresCaptions, setRequiresCaptions] = useState(false);

  const [ODPrescription, setODPrescription] = useState({
    cylinder: "",
    sphere: "",
    axis: "",
  });

  const [OSPrescription, setOSPrescription] = useState({
    cylinder: "",
    sphere: "",
    axis: "",
  });

  const formattedSexOptions = useMemo(
    () => [
      { displayName: t("Male"), value: "Male" },
      { displayName: t("Female"), value: "Female" },
      { displayName: t("Prefer not to say"), value: "Undefined" },
    ],
    [t]
  );

  const priorityLanguageOptions = useMemo(
    () =>
      Object.entries(priorityLanguageMap)
        .map(([_, lang]) => ({ displayName: t(lang.label), value: lang.label }))
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [t]
  );

  const formattedLanguageOptions = useMemo(() => {
    let patientOptions = Object.entries(allLanguageMap)
      .filter(
        ([_, lang]) =>
          !priorityLanguageOptions.find((option) => option.value === lang.value)
      )
      .map(([_, lang]) => ({ displayName: t(lang.label), value: lang.label }))
      .sort((a, b) => a?.displayName?.localeCompare(b?.displayName));

    patientOptions.unshift(...priorityLanguageOptions);
    return patientOptions;
  }, [t, priorityLanguageOptions]);

  const [language, setLanguage] = useState({ displayName: "", value: "" });

  useEffect(() => {
    if (open && !language.value) {
      const defaultPatientLang = formattedLanguageOptions.find(
        (option) =>
          option.value === clinicInfo?.settings?.patientDefaultLanguage
      );
      const defaultClinicLang = formattedLanguageOptions.find(
        (option) => option.value === clinicInfo?.settings?.clinicDefaultLanguage
      );
      const defaultLanguage = { displayName: t("English"), value: "English" };

      setLanguage(defaultPatientLang ?? defaultClinicLang ?? defaultLanguage);
    }
  }, [clinicInfo, formattedLanguageOptions, language.value, open, t]);

  const [birthdate, setBirthdate] = useState<DateTime | null>(null);
  const [birthdateErrorText, setBirthdateErrorText] = useState("");

  const calculatedAgeText = useMemo(() => {
    if (!birthdate) return null;

    const birthdateUtc = getISODate(birthdate);
    const age = calculateYearDifference(birthdateUtc, clinicInfo?.timezone);
    return age !== null ? `${age} ${t("years old")}` : null;
  }, [birthdate, clinicInfo?.timezone, t]);

  const handleChangeDate = useCallback(
    (newValue: DateTime | null) => {
      const timezoneToUse = clinicInfo?.timezone ?? DateTime.local().zoneName;

      const newValueInZone =
        newValue
          ?.setZone(timezoneToUse, { keepLocalTime: true })
          ?.startOf("day") ?? null;

      const today = DateTime.now()
        .setZone(timezoneToUse, { keepLocalTime: true })
        .startOf("day");

      const minDate = today.minus({ years: 120 });

      if (!newValueInZone || !newValueInZone?.isValid) {
        setBirthdateErrorText(t("Invalid Date YYYY-MM-DD"));
      } else if (newValueInZone > today) {
        setBirthdateErrorText(t("Birthdate cannot be in the future"));
      } else if (newValueInZone < minDate) {
        setBirthdateErrorText(
          t(
            "This patient is over 120 years old, which may limit the compatibility with certain visual field exams."
          )
        );
      } else {
        setBirthdateErrorText("");
      }

      setBirthdate(newValueInZone);
    },
    [clinicInfo?.timezone, t]
  );

  // prefills the blank form with the data already extracted from the imported
  // file, so the user is not retyping what the match screen already showed
  useEffect(() => {
    if (!open || firstName || lastName) return;

    setFirstName(exam.patientFirstName ?? "");
    setLastName(exam.patientLastName ?? "");
    setHealthCareId(exam.patientHealthCareId ?? "");

    if (exam.patientDateOfBirth) {
      const parsedBirthdate = DateTime.fromISO(
        getBirthdateOnly(exam.patientDateOfBirth)
      );
      if (parsedBirthdate.isValid) handleChangeDate(parsedBirthdate);
    }

    const normalizedSex = getSexLabel(exam.patientSex);
    const matchedSexOption = formattedSexOptions.find(
      (option) => option.value === normalizedSex
    );
    if (matchedSexOption) setSex(matchedSexOption);
  }, [open, exam, firstName, lastName, handleChangeDate, formattedSexOptions]);

  const handleSetSex = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedSexOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSex({ ...value });
      }
    },
    [formattedSexOptions]
  );

  const handleSetLanguage = useCallback(
    (item: { displayName: string; value: string }) => {
      const value = formattedLanguageOptions.find(
        (option) => option.value === item.value
      );

      if (value) {
        setLanguage({ ...value });
      }
    },
    [formattedLanguageOptions]
  );

  useEffect(() => {
    const matchingPatient = allPatients?.find(
      (patient) =>
        patient.firstName === firstName && patient.lastName === lastName
    );

    if (matchingPatient) {
      setExistingPatient(matchingPatient);
    } else {
      setExistingPatient(undefined);
    }
  }, [allPatients, firstName, lastName]);

  const shouldDisable = useMemo(() => {
    const hasInvalidLanguage = !formattedLanguageOptions.find(
      (option) => option.value === language.value
    );
    const hasInvalidSex = !formattedSexOptions.find(
      (option) => option.value === sex.value
    );

    return (
      !firstName ||
      !lastName ||
      !birthdate ||
      Boolean(birthdateErrorText) ||
      hasInvalidSex ||
      hasInvalidLanguage
    );
  }, [
    birthdate,
    firstName,
    lastName,
    sex,
    language,
    birthdateErrorText,
    formattedLanguageOptions,
    formattedSexOptions,
  ]);

  const resetModalValues = useCallback(() => {
    setFirstName("");
    setLastName("");
    setHealthCareId("");
    setBirthdate(null);
    setLanguage({ displayName: "", value: "" });
    setSex({ displayName: "", value: "" });
    setRequiresCaptions(false);
    setODPrescription({ cylinder: "", sphere: "", axis: "" });
    setOSPrescription({ cylinder: "", sphere: "", axis: "" });
  }, []);

  const handleClose = useCallback(() => {
    resetModalValues();
    onClose();
  }, [onClose, resetModalValues]);

  const handleConfirm = useCallback(() => {
    resetModalValues();

    onConfirm({
      firstName,
      lastName,
      healthCareId,
      dateOfBirth: getISODate(birthdate),
      sex: sex.value,
      language: language.value ?? "English",
      requiresCaptions,
      leftCylinder:
        OSPrescription.cylinder === "" || OSPrescription.cylinder === null
          ? null
          : Number(OSPrescription.cylinder),
      leftSphere:
        OSPrescription.sphere === "" || OSPrescription.sphere === null
          ? null
          : Number(OSPrescription.sphere),
      leftAxis:
        OSPrescription.axis === "" || OSPrescription.axis === null
          ? null
          : Number(OSPrescription.axis),
      rightCylinder:
        ODPrescription.cylinder === "" || ODPrescription.cylinder === null
          ? null
          : Number(ODPrescription.cylinder),
      rightSphere:
        ODPrescription.sphere === "" || ODPrescription.sphere === null
          ? null
          : Number(ODPrescription.sphere),
      rightAxis:
        ODPrescription.axis === "" || ODPrescription.axis === null
          ? null
          : Number(ODPrescription.axis),
    });
  }, [
    firstName,
    lastName,
    healthCareId,
    birthdate,
    sex.value,
    language.value,
    requiresCaptions,
    ODPrescription.axis,
    ODPrescription.cylinder,
    ODPrescription.sphere,
    OSPrescription.axis,
    OSPrescription.cylinder,
    OSPrescription.sphere,
    onConfirm,
    resetModalValues,
  ]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={getLocale(currentLang).locale}
    >
      <DefaultModal
        isOpen={open}
        title={t("Add Patient")}
        onClose={handleClose}
        maxWidth="xs"
        modalActions={
          <Grid
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
            }}
          >
            <Button sx={ModalCustomCancel} onClick={handleClose}>
              {t("Cancel")}
            </Button>
            <Button
              disabled={shouldDisable}
              sx={ModalCustomConfirm}
              onClick={handleConfirm}
            >
              {t("Save")}
            </Button>
          </Grid>
        }
      >
        <Grid display="flex" flexDirection="column" p="16px" gap="8px">
          {existingPatient && (
            <Alert
              variant="warning"
              alertContent={
                <Grid display="flex" flexDirection="column" gap="4px">
                  <Typography fontSize="12px" fontWeight={600}>
                    {t("A patient already exists with the given name.")}
                  </Typography>
                  <Grid display="flex" flexDirection="column">
                    <Typography fontSize="12px">
                      {t("Healthcare Id")}:{" "}
                      {existingPatient?.healthCareId || "N/A"}
                    </Typography>
                    <Typography fontSize="12px">
                      {t("Birthdate")}: {existingBirthDate || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          )}
          <Box display="flex" gap="8px">
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">
                {t("First Name")}
                <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
              </Typography>
              <TextInputField
                id="emr-patient-first-name-input"
                required
                autoFocus
                placeholder={t("Enter a value")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">
                {t("Last Name")}
                <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
              </Typography>
              <TextInputField
                id="emr-patient-last-name-input"
                required
                placeholder={t("Enter a value")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>
          </Box>

          <Box display="flex" gap="8px" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">{t("Healthcare Id")}</Typography>
              <TextInputField
                id="emr-patient-healthcare-id-input"
                placeholder={t("Enter a value")}
                value={healthCareId}
                onChange={(e) => setHealthCareId(e.target.value)}
              />
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {t("Birthdate")}
                  <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
                </span>
                <span>{calculatedAgeText ? `${calculatedAgeText}` : ""}</span>
              </Box>
              <CustomDatePicker
                disableFuture
                openTo="year"
                date={birthdate}
                handleChange={handleChangeDate}
                hasError={Boolean(birthdateErrorText)}
                helperText={birthdateErrorText}
              />
            </Box>
          </Box>

          <Box display="flex" gap="8px" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">
                {t("Sex")}
                <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
              </Typography>
              <SelectDropdown
                id="emr-patient-sex-dropdown"
                dropdownOptions={formattedSexOptions}
                value={sex.displayName}
                handleChange={handleSetSex}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">
                {t("Language")}
                <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
              </Typography>
              <CustomAutocomplete
                id="emr-patient-language-autocomplete"
                data={formattedLanguageOptions}
                value={language}
                onChange={handleSetLanguage}
              />
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap="4px">
            <Typography fontSize="12px">
              {t("Trial Lens Calculation")}
            </Typography>
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "12px",
                border:
                  theme.palette.mode === "dark"
                    ? "1px solid #2A2E44"
                    : "1px solid #5B6D7A",
                borderRadius: "4px",
              }}
            >
              <Box display="flex" flexDirection="column" gap="4px">
                <Typography fontSize="12px">
                  {t("OD Rx")} ({t("Optional")})
                </Typography>
                <TrialPrescriptionInputs
                  prescription={ODPrescription}
                  onPrescriptionChange={setODPrescription}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap="4px">
                <Typography fontSize="12px">
                  {t("OS Rx")} ({t("Optional")})
                </Typography>
                <TrialPrescriptionInputs
                  prescription={OSPrescription}
                  onPrescriptionChange={setOSPrescription}
                />
              </Box>
            </Grid>
          </Box>

          <Grid
            item
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            width="100%"
            height="30px"
          >
            <FormControlLabel
              sx={{
                marginRight: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                  marginLeft: "0px",
                  color: onSurface,
                  "&.Mui-disabled": {
                    color: "#5B6D7A",
                  },
                },
              }}
              control={
                <CustomSwitch
                  checked={requiresCaptions}
                  onChange={(e) => setRequiresCaptions(e.target.checked)}
                  sx={{
                    ...(theme.palette.mode === "dark" && {
                      "& .MuiSwitch-track": {
                        backgroundColor: "#202338",
                      },
                      "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track":
                        {
                          backgroundColor: "#20233843",
                        },
                    }),
                  }}
                />
              }
              label={t("Requires Exam Captions")}
            />
          </Grid>
        </Grid>
      </DefaultModal>
    </LocalizationProvider>
  );
};

export default CreateEMRPatientModal;
